/**
 * Response shapes for `coaching-analytics`. Every section is scoped by the same
 * cohort filter and returns counts with their denominators, so a percentage can
 * be shown without inventing one.
 */

/** The filter bar's sentinel for "not narrowed". */
export const ALL_FILTER_VALUE = 'all';

/** The filter bar's sentinel for "no value recorded". */
export const NO_DATA_FILTER_VALUE = 'none';

/** A dimension narrows to one value or to any of several; an empty list is not narrowed. */
export type AnalyticsFilterSelection = string | readonly string[];

export type AnalyticsFilter = Readonly<{
  institution?: AnalyticsFilterSelection;
  grade?: AnalyticsFilterSelection;
  programs?: AnalyticsFilterSelection;
  enrollment?: AnalyticsFilterSelection;
  coaches?: AnalyticsFilterSelection;
  gender?: AnalyticsFilterSelection;
  ethnicity?: AnalyticsFilterSelection;
  cohorts?: AnalyticsFilterSelection;
}>;

export type CountOfTotal = Readonly<{ count: number; total: number }>;

export type CountWithPercentage = CountOfTotal & Readonly<{ percentage: number }>;

export type BucketCount = Readonly<{ id: string; count: number }>;

export type FilterOptionsResponse = Readonly<{
  institutions: readonly Readonly<{ id: number; name: string }>[];
  grades: readonly Readonly<{ value: string; name: string | null; count: number }>[];
  programs: readonly Readonly<{ value: string; count: number }>[];
  enrollment_types: readonly Readonly<{
    value: string;
    name: string | null;
    level: string | null;
    count: number;
  }>[];
  /** `name` is null for the "none" option. */
  genders: readonly Readonly<{ value: string; name: string | null; count: number }>[];
  ethnicities: readonly Readonly<{ value: string; name: string | null; count: number }>[];
  cohorts: readonly Readonly<{
    value: string;
    name: string | null;
    institution_id: number | null;
    count: number;
  }>[];
  /** Students each coach reaches within the other filters; coaches reaching none are absent. */
  coaches: readonly Readonly<{ value: string; name: string; count: number }>[];
}>;

export type CoachCounts = Readonly<{
  total: number;
  backrs: number;
  institutional: number;
}>;

/** A grade somebody is in; `name` is null for the `none` bucket and for a grade with no label. */
export type GradeBucketCount = Readonly<{ id: string; name: string | null; count: number }>;

export type OverviewResponse = Readonly<{
  students: Readonly<{
    total: number;
    by_stage: readonly BucketCount[];
    by_program: readonly BucketCount[];
    by_grade: readonly GradeBucketCount[];
  }>;
  recently_active: CountWithPercentage;
  /** Institution scope only — a coach's own population has no coach roster. */
  coaches?: CoachCounts;
  /** Coach scope only — the viewer's own posts and comments by month. */
  my_activity?: Readonly<{ monthly: ActivitySeries }>;
  completions: Readonly<{
    blueprint: CountWithPercentage;
    are_you_on_track: CountWithPercentage;
    life_readiness: CountWithPercentage;
  }>;
}>;

export type CareerPathFitCategory = Readonly<{
  id: string;
  category_id: number;
  name: string;
  short_name: string | null;
  students: Readonly<{ strong: number; medium: number; low: number }>;
  results: Readonly<{ strong: number; medium: number; low: number }>;
}>;

export type BlueprintQuestionOption = Readonly<{
  id: number;
  answer: string;
  count: number;
}>;

/** One visible Blueprint question with how many latest Blueprints chose each option. */
export type BlueprintQuestion = Readonly<{
  id: number;
  /** The question's `calculator_key`, stable across environments where `id` is not. */
  key: string;
  title: string;
  question: string;
  is_multi_select: boolean;
  answered: number;
  options: readonly BlueprintQuestionOption[];
}>;

export type BlueprintResponse = Readonly<{
  students: Readonly<{ total: number }>;
  unique_blueprints: CountWithPercentage;
  entrepreneurship_recommended: CountWithPercentage;
  target_career_tracks: Readonly<{ total: number; tiers: readonly BucketCount[] }>;
  career_path_fit: Readonly<{
    total: number;
    categories: readonly CareerPathFitCategory[];
  }>;
  answers: Readonly<{ total: number; questions: readonly BlueprintQuestion[] }>;
}>;

export type CareerTrack = Readonly<{
  id: string;
  career_track_id: number;
  name: string;
  rank: number;
}>;

/** Counts keyed by career track code. */
export type TrackCounts = Readonly<Record<string, number>>;

export type MeasureBins = Readonly<{
  bin_size: number;
  with_score: number;
  no_score_on_file: number;
  bins: readonly Readonly<{
    min: number;
    max: number;
    total: number;
    tracks: TrackCounts;
  }>[];
}>;

export type RigorBand = Readonly<{
  id: string | number;
  name: string;
  total: number;
  tracks: TrackCounts;
  /** Coursework rigor only: the band the GPA requirement table scores it as. */
  scored_as?: string;
  /** Major rigor only. */
  min_score?: number;
}>;

export type MeasureId = 'gpa' | 'sat' | 'act';

export type AcademicTab = Readonly<{
  total: number;
  /** High school carries all three; post-secondary carries GPA only. */
  measures: Readonly<Partial<Record<MeasureId, MeasureBins>>>;
  /** Post-secondary only: the selectivity of the college named on the assessment. */
  selectivity?: Readonly<{ bands: readonly RigorBand[] }>;
  rigor: Readonly<{
    type: 'coursework' | 'major';
    bands: readonly RigorBand[];
    not_on_file: number;
  }>;
}>;

export type AyotTenure = 'high_school' | 'post_secondary';

export type AyotHeatCounts = Readonly<{ total: number } & Record<string, number>>;

export type AyotHeatDimension = Readonly<{
  /** Dimension id as a string, or the academic measure id (`gpa`, `test`, `rigor`, `major_rigor`, `selectivity`). */
  id: string;
  dimension_id: number | null;
  name: string;
  /**
   * `level` rows bucket into score levels; `track` rows bucket into career
   * tracks; `band` rows bucket into the measure's own bands, lowest first.
   */
  kind: 'level' | 'track' | 'band';
  buckets: readonly string[];
  /** Display name per bucket for `band` rows; null otherwise. */
  bucket_names: Readonly<Record<string, string>> | null;
  tenures: readonly AyotTenure[];
  /** Per tenure, the only grades the row is scored for; null when every grade is. */
  grade_ids: Readonly<Partial<Record<AyotTenure, readonly number[]>>> | null;
  definition: Readonly<Partial<Record<AyotTenure, string>>>;
  standards: Readonly<Partial<Record<AyotTenure, Readonly<Record<string, string>>>>>;
  criteria: readonly Readonly<{
    grade_id: number;
    level: string;
    text: string;
    short_text: string | null;
  }>[];
  counts: Readonly<{
    all: AyotHeatCounts;
    by_grade: Readonly<Record<string, AyotHeatCounts>>;
  }>;
}>;

export type AyotHeatGrade = Readonly<{
  id: number;
  name: string;
  value: string;
  /** Null for a grade on neither the high school nor the post-secondary scale. */
  tenure: AyotTenure | null;
  total: number;
}>;

export type AyotHeatMap = Readonly<{
  total: number;
  excluded: Readonly<{ semi_skilled: number }>;
  no_grade: number;
  grades: readonly AyotHeatGrade[];
  levels: readonly string[];
  tracks: readonly string[];
  by_tenure: readonly BucketCount[];
  categories: readonly Readonly<{
    id: string;
    category_id: number;
    name: string;
    shortname: string | null;
    dimensions: readonly AyotHeatDimension[];
  }>[];
  /** Minimum GPA per career track the scorer reads, keyed by the band rows' bucket ids. */
  gpa_requirements: Readonly<{
    high_school: readonly Readonly<{
      rigor: string | null;
      track: string | null;
      minimum_gpa: number;
    }>[];
    post_secondary: readonly Readonly<{
      selectivity: string | null;
      major_rigor: string | null;
      track: string | null;
      minimum_gpa: number;
    }>[];
  }>;
}>;

export type AyotResponse = Readonly<{
  students: Readonly<{ total: number }>;
  heat_map: AyotHeatMap;
  assessed: CountWithPercentage;
  tracks: readonly CareerTrack[];
  aspiration_vs_reality: Readonly<{
    total: number;
    excluded: Readonly<{
      no_blueprint: number;
      unrecognized_tier: number;
      unranked_track: number;
    }>;
    statuses: readonly BucketCount[];
    matrix: Readonly<{
      aspirations: readonly Readonly<{
        aspiration: string;
        total: number;
        tracks: TrackCounts;
      }>[];
      track_totals: TrackCounts;
    }>;
  }>;
  academic_profile: Readonly<{
    high_school: AcademicTab;
    post_secondary: AcademicTab;
    unclassified: number;
  }>;
  non_academic_profile: Readonly<{
    total: number;
    excluded: Readonly<{
      no_blueprint: number;
      unrecognized_tier: number;
      no_dimension_scores: number;
    }>;
    statuses: readonly BucketCount[];
    categories: readonly Readonly<{
      id: string;
      category_id: number;
      name: string;
      shortname: string | null;
      counts_toward_verdict: boolean;
      total: number;
      statuses: readonly BucketCount[];
    }>[];
  }>;
}>;

export type ReadinessCategory = Readonly<{
  id: string;
  category_id: number;
  name: string;
  sequence: number | null;
  answers: Readonly<{
    total: number;
    bands: readonly BucketCount[];
  }>;
  coverage: Readonly<{
    questions: number;
    answered: number;
    answerable: number;
    unanswered: number;
  }>;
}>;

export type ReadinessFacet = Readonly<{
  id: number;
  slug: string | null;
  name: string;
  description: string | null;
  answers: Readonly<{
    total: number;
    bands: readonly BucketCount[];
  }>;
}>;

export type ReadinessFacetCategory = Readonly<{
  id: string;
  category_id: number;
  name: string;
  description: string | null;
  sequence: number | null;
  facets: readonly ReadinessFacet[];
}>;

export type LifeReadinessResponse = Readonly<{
  students: Readonly<{ total: number }>;
  completion: Readonly<{
    total: number;
    statuses: readonly BucketCount[];
    questions: Readonly<{ visible: number }>;
  }>;
  readiness_by_category: Readonly<{
    students_started: number;
    bands: readonly Readonly<{ id: string; levels: readonly number[] }>[];
    categories: readonly ReadinessCategory[];
  }>;
  readiness_by_facet: Readonly<{
    bands: readonly Readonly<{ id: string; levels: readonly number[] }>[];
    categories: readonly ReadinessFacetCategory[];
  }>;
}>;

export type ActivityBucket = Readonly<{
  index: number;
  start: string;
  posts: number;
  goals: number;
  comments: number;
}>;

export type ActivitySeries = Readonly<{
  buckets: number;
  series: readonly ActivityBucket[];
}>;

export type LoginRecency = Readonly<{
  total: number;
  buckets: Readonly<{
    last_30: CountOfTotal;
    days_31_60: CountOfTotal;
    days_61_90: CountOfTotal;
    over_90: CountOfTotal;
  }>;
}>;

export type ProfileSignals = Readonly<{
  total: number;
  signals: readonly Readonly<{ id: string; count: number }>[];
}>;

export type EngagementResponse = Readonly<{
  students: Readonly<{ total: number }>;
  recently_active: LoginRecency;
  activity: Readonly<{ monthly: ActivitySeries }>;
  networks: readonly Readonly<{
    group_id: number;
    name: string;
    type: string | null;
    count: number;
  }>[];
  profile: ProfileSignals;
  completions: Readonly<{
    blueprint: CountWithPercentage;
    are_you_on_track: CountWithPercentage;
    life_readiness: CountWithPercentage;
  }>;
  bookmarks: Readonly<{
    types: readonly Readonly<{ id: string; bookmarks: number; students: number }>[];
  }>;
}>;

export type CoachRow = Readonly<{
  id: number;
  first_name: string | null;
  last_name: string | null;
  type: 'backrs' | 'institutional';
  caseload: number;
  total_caseload: number;
}>;

export type CoachTypeSummary = Readonly<{
  coaches: readonly CoachRow[];
  coach_count: number;
  assignments: number;
  average_per_coach: number;
  students_with_coach: number;
}>;

export type CoachingResponse = Readonly<{
  by_type: Readonly<{
    institutional: CoachTypeSummary;
    backrs: CoachTypeSummary;
  }>;
  coach_counts: CoachCounts;
  coverage: Readonly<{
    students: number;
    with_coach: number;
    without_coach: number;
  }>;
  /** The institution's own coaches in the roster; Backrs coaches are never included. */
  institutional_coaches: Readonly<{
    total: number;
    recently_active: LoginRecency;
    profile: ProfileSignals;
    activity: Readonly<{ monthly: ActivitySeries }>;
  }>;
}>;

export type ToolUsageResponse = Readonly<{
  students: Readonly<{
    total: number;
    /** Used at least one tool. Not the sum of the per-tool counts, which overlap. */
    with_any_tool: number;
  }>;
  tools: readonly Readonly<{
    tool: string;
    unique_students: number;
    total_sessions: number;
  }>[];
}>;

/** Operators accepted by the student list's `where` clauses. */
export type StudentWhereOp =
  | 'eq'
  | 'neq'
  | 'in'
  | 'not_in'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'is_null'
  | 'is_not_null';

/** One filter clause; `key` selects an entry of a map-kind field. */
export type StudentWhereClause = Readonly<{
  field: string;
  key?: string;
  op: StudentWhereOp;
  value?: string | number | boolean | readonly (string | number)[];
}>;

/** A student field value as returned by the list: scalars, lists, or a per-key map. */
export type StudentFieldValue =
  | string
  | number
  | boolean
  | null
  | readonly string[]
  | Readonly<Record<string, string | readonly string[] | null>>;

export type StudentRow = Readonly<Record<string, StudentFieldValue>>;

export type StudentListRequest = Readonly<{
  filter?: AnalyticsFilter;
  where?: readonly StudentWhereClause[];
  fields?: readonly string[];
  sort?: Readonly<{ by: string; direction?: 'asc' | 'desc' }>;
  limit?: number;
  offset?: number;
}>;

export type StudentListResponse = Readonly<{
  population_total: number;
  matched_total: number;
  offset: number;
  returned: number;
  students: readonly StudentRow[];
  notes?: readonly string[];
}>;

export type StudentFieldKey = Readonly<{ key: string; name: string }>;
